import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { io } from "socket.io-client";
import { useAuth, API } from "../App";
import {
  MessageSquare, Send, User, Clock, CheckCircle, XCircle,
  Loader2, RefreshCw, ArrowLeft, UserRound, Wifi, WifiOff
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLiveSupport = ({ onClose }) => {
  const { user, token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Connect to Socket.IO
  useEffect(() => {
    const socket = io(BACKEND_URL, {
      path: '/api/ws',
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Admin connected to Socket.IO');
      setIsConnected(true);
      // Join admin support room
      socket.emit('admin_join_support', {});
    });

    socket.on('disconnect', () => {
      console.log('Admin disconnected');
      setIsConnected(false);
    });

    socket.on('support_chats_list', (data) => {
      setChats(data.chats || []);
    });

    socket.on('new_chat_waiting', (data) => {
      setChats(prev => {
        const exists = prev.find(c => c.session_id === data.session_id);
        if (exists) return prev;
        return [...prev, {
          session_id: data.session_id,
          status: 'waiting',
          created_at: new Date().toISOString(),
          message_count: 0,
          has_user: true
        }];
      });
    });

    socket.on('chat_joined', (data) => {
      setMessages(data.messages || []);
    });

    socket.on('new_message', (data) => {
      if (data.session_id === selectedChat?.session_id) {
        setMessages(prev => [...prev, data.message]);
      }
      // Update message count in chat list
      setChats(prev => prev.map(c => 
        c.session_id === data.session_id 
          ? { ...c, message_count: (c.message_count || 0) + 1 }
          : c
      ));
      setIsTyping(false);
    });

    socket.on('user_typing', (data) => {
      if (data.session_id === selectedChat?.session_id && !data.is_admin) {
        setIsTyping(true);
      }
    });

    socket.on('user_stopped_typing', (data) => {
      if (data.session_id === selectedChat?.session_id) {
        setIsTyping(false);
      }
    });

    socket.on('user_disconnected', (data) => {
      if (data.session_id === selectedChat?.session_id) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          role: 'system',
          content: "L'utilisateur s'est déconnecté",
          timestamp: new Date().toISOString()
        }]);
      }
      setChats(prev => prev.map(c =>
        c.session_id === data.session_id
          ? { ...c, has_user: false }
          : c
      ));
    });

    return () => {
      socket.disconnect();
    };
  }, [token, selectedChat?.session_id]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Join a specific chat
  const joinChat = (chat) => {
    setSelectedChat(chat);
    setMessages([]);
    socketRef.current?.emit('admin_join_chat', { session_id: chat.session_id });
    
    // Update chat status in list
    setChats(prev => prev.map(c =>
      c.session_id === chat.session_id
        ? { ...c, status: 'active' }
        : c
    ));
  };

  // Send message
  const sendMessage = () => {
    if (!inputValue.trim() || !selectedChat) return;

    socketRef.current?.emit('send_message', {
      session_id: selectedChat.session_id,
      message: inputValue.trim()
    });

    setInputValue("");
  };

  // Handle typing indicator
  const handleTyping = () => {
    if (socketRef.current?.connected && selectedChat) {
      socketRef.current.emit('typing_start', { session_id: selectedChat.session_id });
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current?.emit('typing_stop', { session_id: selectedChat.session_id });
      }, 2000);
    }
  };

  // Close chat
  const closeChat = () => {
    if (!selectedChat) return;
    socketRef.current?.emit('close_chat', { session_id: selectedChat.session_id });
    setChats(prev => prev.map(c =>
      c.session_id === selectedChat.session_id
        ? { ...c, status: 'closed' }
        : c
    ));
    setSelectedChat(null);
    setMessages([]);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="h-full flex bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden">
      {/* Chat List Sidebar */}
      <div className="w-80 border-r border-zinc-800 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-white">Support Live</h3>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <span className="flex items-center gap-1 text-xs text-green-400">
                  <Wifi size={14} />
                  Connecté
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-red-400">
                  <WifiOff size={14} />
                  Déconnecté
                </span>
              )}
            </div>
          </div>
          <p className="text-xs text-zinc-500">{chats.filter(c => c.status === 'waiting').length} en attente</p>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto">
          {chats.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare size={32} className="mx-auto mb-2 text-zinc-600" />
              <p className="text-sm text-zinc-500">Aucune conversation</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {chats.filter(c => c.status !== 'closed').map((chat) => (
                <button
                  key={chat.session_id}
                  onClick={() => joinChat(chat)}
                  className={`w-full p-4 text-left hover:bg-zinc-800/50 transition-colors ${
                    selectedChat?.session_id === chat.session_id ? 'bg-zinc-800' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-white">
                      {chat.session_id.slice(0, 12)}...
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      chat.status === 'waiting' 
                        ? 'bg-yellow-500/20 text-yellow-400' 
                        : chat.status === 'active'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-zinc-500/20 text-zinc-400'
                    }`}>
                      {chat.status === 'waiting' ? 'En attente' : chat.status === 'active' ? 'Actif' : 'Fermé'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-zinc-500">
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {new Date(chat.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span>{chat.message_count || 0} msg</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setSelectedChat(null); setMessages([]); }}
                  className="p-2 rounded-lg hover:bg-zinc-800 transition-colors md:hidden"
                >
                  <ArrowLeft size={18} />
                </button>
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <User size={18} className="text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-white">{selectedChat.session_id.slice(0, 16)}...</p>
                  <p className="text-xs text-zinc-500">
                    {selectedChat.has_user ? 'Utilisateur connecté' : 'Utilisateur déconnecté'}
                  </p>
                </div>
              </div>
              <button
                onClick={closeChat}
                className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 transition-colors"
              >
                Clôturer
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'admin' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-start gap-2 max-w-[70%] ${msg.role === 'admin' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                      msg.role === 'admin' 
                        ? 'bg-green-500/20' 
                        : msg.role === 'system'
                        ? 'bg-yellow-500/20'
                        : msg.role === 'assistant'
                        ? 'bg-pink-500/20'
                        : 'bg-blue-500/20'
                    }`}>
                      {msg.role === 'admin' ? (
                        <UserRound size={14} className="text-green-400" />
                      ) : msg.role === 'system' ? (
                        <Clock size={14} className="text-yellow-400" />
                      ) : msg.role === 'assistant' ? (
                        <MessageSquare size={14} className="text-pink-400" />
                      ) : (
                        <User size={14} className="text-blue-400" />
                      )}
                    </div>
                    <div className={`rounded-2xl px-4 py-2.5 ${
                      msg.role === 'admin'
                        ? 'bg-green-500/20 border border-green-500/30 text-green-100'
                        : msg.role === 'system'
                        ? 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-200'
                        : msg.role === 'assistant'
                        ? 'bg-pink-500/10 border border-pink-500/20 text-pink-200'
                        : 'bg-zinc-800 border border-zinc-700 text-zinc-200'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className="text-xs opacity-50 mt-1">
                        {new Date(msg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <User size={14} className="text-blue-400" />
                  </div>
                  <div className="bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-zinc-800">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    handleTyping();
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="Écrivez votre réponse..."
                  className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-green-500/50"
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputValue.trim()}
                  className="w-11 h-11 rounded-xl bg-green-500 text-white flex items-center justify-center disabled:opacity-50 hover:bg-green-600 transition-colors"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare size={48} className="mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400">Sélectionnez une conversation</p>
              <p className="text-sm text-zinc-500 mt-1">pour commencer à répondre</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLiveSupport;
